import React, { memo, useState, useEffect } from "react"
import { useFormContext } from "react-hook-form"
import { useLocation } from "@reach/router"
import API from "@aws-amplify/api"

import { months, days } from "@data/date"
import certificateTypes from "@data/certificateType"
import countryCodes from "@data/countryCode"
import { getVarFromURL } from "@helpers"
import { awsConfig } from "@configs"
import { useTranslate } from "@hooks/translate"

import Label from "@components/Form/Label"
import Input from "@components/Form/Input"
import Radio from "@components/Form/Radio"
import Select from "@components/Form/Select"
import CertUpload from "@components/Form/CertUpload"
import WarningMsg from "@components/Form/WarningMsg"
import RegionAndCity from "@components/Form/RegionAndCity"

const ContactSection = ({
  handleChangeFile = () => {},
  certSrc,
  setCertSrc,
  taxIdProofSrc,
  setTaxIdProofSrc,
  ...props
}) => {
  const translate = useTranslate()
  const location = useLocation()
  const {
    register,
    watch,
    errors,
    setValue,
    setError,
    clearError,
  } = useFormContext()

  const [sponsorMsg, setSponsorMsg] = useState("")
  const [loadedUrlParams, setLoadedUrlParams] = useState(false)

  const distributor_number = watch("distributor_number")
  const isCountryCodeRequired = watch("country") === "OTHERS"

  useEffect(() => {
    if (typeof window !== "undefined") {
      const setData = (name, value, callback) => {
        setValue(name, value, true)
        window.localStorage.setItem(name, value)
      }
      const pathname = location.pathname
    }
  }, [])

  useEffect(() => {
    if (loadedUrlParams) {
      getDistributor()
    }
  }, [loadedUrlParams])

  const handleChangeCountryCode = countryCode => {
    const selectedOption = countryCodes.find(
      country => country.value === countryCode
    )
    const isOthers = selectedOption && selectedOption.value === "OTHERS"
    const isEmpty = countryCode === ""

    setValue("country", countryCode)
    setValue(
      "telephone_country_code",
      !isOthers && !isEmpty ? selectedOption.code : ""
    )
    clearError('telephone_country_code');
  }

  const getDistributor = async () => {
    if (
      !distributor_number ||
      distributor_number?.length < 9 ||
      !distributor_number.match(/^\d+$/)
    ) {
      return false
    }
    clearError(["distributor_number", "distributor_number_validated"])
    setValue("distributor_number_validated", 1)
  }

  return (
    <div className="box-theme" id="wrapper-reg-personal-info">
      <div className="box-theme-title">{translate("Profile")}</div>

      <div className="box-theme-content">
        <div className="row">
          <div className="col-md-4">
            <div className="form-group">
              <Input
                ref={register({
                  required: "This is a required field.",
                  pattern: {
                    value: /^\d+$/,
                    message:
                      "Referral number or distributor ID must be number.",
                  },
                  validate: value =>
                    value.length === 9 ||
                    "Please enter a valid Distributor number.",
                })}
                required
                hasError={
                  errors.distributor_number || errors.distributor_number
                }
                warningMsg={
                  errors.distributor_number ? errors.distributor_number?.message : ""
                }
                label="Distributor ID"
                name="distributor_number"
                id="distributor_number"
                onBlur={getDistributor}
                inputOptions={{ maxLength: 9 }}
              />
              {sponsorMsg && (
                <p className="help-block input-hint">{translate(sponsorMsg)}</p>
              )}

              <input
                ref={register({
                  validate: value =>
                    value === "1" ||
                    "Sorry, distributor number does not exist.",
                })}
                name="distributor_number_validated"
                id="distributor-number-validated"
                type="hidden"
              />
              {distributor_number && errors.distributor_number_validated && (
                <WarningMsg msg={errors.distributor_number_validated?.message} />
              )}
            </div>
          </div>
        </div>
        <div className="customer-name row">
          <div className="form-group col-lg-4 col-md-6">
            <Input
              ref={register({
                required: "This is a required field.",
                validate: {
                  noNumberOrSymbol: value =>
                    !value.match(
                      /([%\$#\*@!^&()_+={}\\|\"\':;<>,.?\/`~0-9]+)/
                    ) || "First name cannot contain number or symbol.",
                },
              })}
              required
              hasError={errors.firstname}
              warningMsg={errors.firstname?.message}
              name="firstname"
              id="firstname"
              label="custom_first_name_label"
              title="First Name"
              inputOptions={{ maxLength: 100 }}
            />
          </div>
          <div className="form-group col-lg-4 col-md-6">
            <Input
              ref={register({
                required: "This is a required field.",
                validate: {
                  noNumberOrSymbol: value =>
                    !value.match(
                      /([%\$#\*@!^&()_+={}\\|\"\':;<>,.?\/`~0-9]+)/
                    ) || "Last name cannot contain number or symbol.",
                },
              })}
              required
              hasError={errors.lastname}
              warningMsg={errors.lastname?.message}
              name="lastname"
              id="lastname"
              label="custom_last_name_label"
              title="Last Name"
              inputOptions={{ maxLength: 100 }}
            />
          </div>
        </div>
        <div className="row">
          <div className="form-group col-lg-4 col-md-6">
            <Input
              ref={register({
                validate: {
                  noNumberOrSymbol: value =>
                    !value.match(
                      /([%\$#\*@!^&()_+={}\\|\"\':;<>,.?\/`~0-9]+)/
                    ) || "First name cannot contain number or symbol.",
                },
              })}
              hasError={errors.chinese_firstname}
              warningMsg={errors.chinese_firstname?.message}
              name="chinese_firstname"
              id="chinese_firstname"
              label="Firstname in Chinese"
              title="Firstname in Chinese"
              inputOptions={{ maxLength: 30 }}
            />
          </div>
          <div className="form-group col-lg-4 col-md-6">
            <Input
              ref={register({
                validate: {
                  noNumberOrSymbol: value =>
                    !value.match(
                      /([%\$#\*@!^&()_+={}\\|\"\':;<>,.?\/`~0-9]+)/
                    ) || "Last name cannot contain number or symbol.",
                },
              })}
              hasError={errors.chinese_lastname}
              warningMsg={errors.chinese_lastname?.message}
              name="chinese_lastname"
              id="chinese_lastname"
              label="Lastname in Chinese"
              title="Lastname in Chinese"
              inputOptions={{ maxLength: 30 }}
            />
          </div>
        </div>

        <hr />

        {/* Email & Phone */}
        <div className="row">
          <div className="col-md-4">
            <div className="form-group">
              <Input
                ref={register({
                  pattern: {
                    value: /^[-a-z0-9~!$%^&*_=+}{\'?]+(\.[-a-z0-9~!$%^&*_=+}{\'?]+)*@([a-z0-9_][-a-z0-9_]*(\.[-a-z0-9_]+)*\.(aero|arpa|biz|com|coop|edu|gov|info|int|mil|museum|name|net|org|pro|travel|mobi|[a-z][a-z])|([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}))(:[0-9]{1,5})?$/i,
                    message:
                      "Please enter a valid email address. For example johndoe@domain.com.",
                  },
                })}
                hasError={errors.secondary_email}
                warningMsg={errors.secondary_email?.message}
                name="secondary_email"
                id="secondary_email"
                label="Email"
                title={translate("Email")}
                inputOptions={{ maxLength: 40 }}
              />
            </div>
          </div>
          <div className="col-md-8">
            <div className="row wrapper-tel-input">
              <Label
                labelClass="col-12"
                forId="country"
                label="Mobile Number"
                required
              />
              <div className="col-md-5 col-12 form-group">
                <Select
                  ref={register({ required: true })}
                  required
                  hasError={errors.country}
                  name="country"
                  id="country"
                  title="Mobile Number Country Code"
                  placeholder="Country Code"
                  options={countryCodes}
                  // value={country}
                  onChange={handleChangeCountryCode}
                />
              </div>
              <div className="col-md-3 col-4 form-group">
                <Input
                  ref={register({
                    required:
                      isCountryCodeRequired &&
                      "Please enter a valid country code",
                    pattern: {
                      value: /^\d+$/,
                      message: "Please enter a valid country code",
                    },
                  })}
                  required={isCountryCodeRequired}
                  hasError={errors.telephone_country_code}
                  warningMsg={errors.telephone_country_code?.message}
                  name="telephone_country_code"
                  title="Mobile Number Country Code"
                  placeholder="Country Code"
                  inputOptions={{
                    maxLength: 5,
                    type: "tel",
                    // pattern: '[0-9]*',
                    readOnly: !isCountryCodeRequired,
                    inputMode: "numeric",
                  }}
                />
              </div>
              <div className="col-md-4 col-8 form-group">
                <Input
                  ref={register({
                    required: "This is a required field.",
                    validate: {
                      validPhoneNumber: value => {
                        const country = watch("country")
                        let isValid = true
                        if (country === "HK") {
                          isValid =
                            value.match(/^\d{8}$/) ||
                            value.match(/^\d{4}-?\d{4}$/)
                        } else if (country === "MO") {
                          isValid =
                            value.match(/^6\d{7}$/) ||
                            value.match(/^6\d{3}-?\d{4}$/)
                        } else if (country === "OTHERS") {
                          isValid = value.match(/^(\d+-?)+\d+$/)
                        } else {
                          isValid = value.match(/^(\d+-?)+\d+$/)
                        }
                        return isValid || "Please enter a valid phone number"
                      },
                    },
                  })}
                  required
                  hasError={errors.telephone}
                  name="telephone"
                  title={translate("Mobile Number")}
                  placeholder="Mobile Number"
                  inputOptions={{
                    maxLength: 15,
                    type: "tel",
                    // pattern: '[0-9]*',
                    inputMode: "numeric",
                  }}
                  warningMsg={errors.telephone?.message}
                />
              </div>
              <p className="help-block col-12">{translate("telephone_hint")}</p>
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="row">
          <div className="col-md-8 form-group">
            <Input
              ref={register({ required: true })}
              required
              hasError={errors.street1}
              name="street1"
              label="Address"
              id="street1"
              title={`${translate("Contact Address")} 1`}
              placeholder={`${translate("Contact Address")} 1`}
              inputClass="form-control  required-entry"
              inputOptions={{ maxLength: 130 }}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-md-8 form-group">
            <Input
              ref={register}
              hasError={errors.street2}
              name="street2"
              title={`${translate("Contact Address")} 2`}
              placeholder={`${translate("Contact Address")} 2`}
              inputClass="form-control"
              inputOptions={{ maxLength: 140 }}
            />
          </div>
        </div>

        {/* Region & City */}
        <div className="row">
          <div className="col-md-10">
            <RegionAndCity />
            <div className="row">
              <div className="col-sm-6 form-group">
                <Input
                  ref={register}
                  hasError={errors.postcode}
                  name="postcode"
                  id="postcode"
                  label="ZIP/Postal Code"
                  title={translate("ZIP/Postal Code")}
                  inputOptions={{ maxLength: 10 }}
                />
              </div>
            </div>
         </div>
        </div>
      </div>
    </div>
  )
}

export default memo(ContactSection);
